import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Markdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bot, Maximize2, Minimize2, RotateCcw, Send, X } from "lucide-react";
import { sendChatbotMessage, ChatMessage, ChatbotChartHint } from "../../services/chatbot.service";
import { useChatbot } from "../../utils/context/ChatbotContext";
import ResponseActions from "./ResponseActions";
import "./ChatbotWidget.css";

interface ChatbotWidgetProps {
  botName?: string;
  welcomeMessage?: string;
  suggestedPrompts?: string[];
}

/** Flatten react-markdown cell children to plain text so we can put the full value in a `title` tooltip. */
const nodeToText = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (typeof node === "object" && "props" in node) return nodeToText((node as { props?: { children?: ReactNode } }).props?.children);
  return "";
};

// Custom table cell renderers: keep each cell on one line and truncate with an ellipsis, exposing the
// full value as a native tooltip (`title`) so wide data still fits the narrow panel. Paired with
// `table-layout: fixed` in the CSS.
const markdownComponents: Components = {
  td: ({ children }) => {
    const text = nodeToText(children).trim();
    return <td className="chatbot-cell" title={text || undefined}>{children}</td>;
  },
  th: ({ children }) => {
    const text = nodeToText(children).trim();
    return <th className="chatbot-cell" title={text || undefined}>{children}</th>;
  },
};

interface UiMessage extends ChatMessage {
  /** Set on assistant turns the backend flagged as a low-confidence clarification request. */
  needsClarification?: boolean;
  /** Optional chart directive the assistant emitted for this answer. */
  chart?: ChatbotChartHint | null;
  /** Id of the AiInput log row this answer was persisted as; used to submit a thumbs up/down rating. */
  aiInputId?: number | null;
}

/** Report name for an assistant message = the user question that prompted it (nearest earlier user turn). */
const reportNameForIndex = (messages: UiMessage[], index: number): string => {
  for (let i = index - 1; i >= 0; i--) {
    if (messages[i].role === "user") {
      const q = messages[i].content.trim().replace(/\s+/g, " ");
      return q.length > 80 ? `${q.slice(0, 77)}…` : q || "Chatbot Report";
    }
  }
  return "Chatbot Report";
};

const HISTORY_KEY = "chatbot_history";

const DEFAULT_PROMPTS = [
  "Show vacant units",
  "Overdue rent payments",
  "Open service requests",
  "What's my occupancy rate?",
];

// Rotating engagement messages shown while the assistant works (the backend call is a single
// synchronous request, so these keep the user engaged client-side; true per-tool progress would
// require server-sent events — deferred).
const LOADING_STATUSES = [
  "Thinking…",
  "Looking through your portfolio…",
  "Crunching the numbers…",
  "Putting your answer together…",
];
const LONG_RUN_STATUS = "Still working — this one's taking a little longer…";
const LONG_RUN_AFTER_TICKS = 5; // ~12.5s at the 2.5s tick

const loadHistory = (): UiMessage[] => {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/** Map the current route to a short human-readable screen name sent to the assistant. */
const deriveScreenContext = (pathname: string): string => {
  const p = pathname.toLowerCase();
  const rules: Array<[string, string]> = [
    ["platform-settings", "Platform settings"],
    ["organization", "Organization settings"],
    ["service-request", "Service requests"],
    ["servicerequest", "Service requests"],
    ["contractor", "Contractors"],
    ["propert", "Properties"],
    ["unit", "Units"],
    ["tenant", "Tenants"],
    ["lease", "Leases"],
    ["payment", "Payments"],
    ["transaction", "Transactions"],
    ["report", "Reports"],
    ["dashboard", "Dashboard"],
  ];
  for (const [fragment, label] of rules) {
    if (p.includes(fragment)) return label;
  }
  if (p === "/" || p === "") return "Dashboard";
  return `Page ${pathname}`;
};

const isAbortError = (err: unknown): boolean => {
  const name = (err as { name?: string })?.name;
  return name === "CanceledError" || name === "AbortError";
};

const ChatbotWidget = ({
  botName = "AI Assistant",
  welcomeMessage = "Hi! Ask me anything about your properties, tenants, leases, or financials.",
  suggestedPrompts = DEFAULT_PROMPTS,
}: ChatbotWidgetProps): JSX.Element => {
  const location = useLocation();

  // Open/close state is owned by ChatbotContext so the header launcher (CustomTopBar) can drive it.
  const { isOpen, close } = useChatbot();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState<UiMessage[]>(loadHistory);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [cancelledNote, setCancelledNote] = useState(false);
  const [statusTick, setStatusTick] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Persist conversation for the session (survives navigation, resets on new session).
  useEffect(() => {
    try {
      sessionStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
    } catch {
      /* ignore quota errors */
    }
  }, [messages]);

  // Auto-scroll to the latest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, isOpen]);

  // Drive the rotating "working…" status while a request is in flight.
  useEffect(() => {
    if (!isLoading) {
      setStatusTick(0);
      return;
    }
    const id = window.setInterval(() => setStatusTick((t) => t + 1), 2500);
    return () => window.clearInterval(id);
  }, [isLoading]);

  // Abort any in-flight request if the widget unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  };

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    setHasError(false);
    setUnavailableMessage(null);
    setCancelledNote(false);

    const history: ChatMessage[] = messages.map((m) => ({ role: m.role, content: m.content }));
    const nextMessages: UiMessage[] = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);

    try {
      const res = await sendChatbotMessage(trimmed, history, {
        screenContext: deriveScreenContext(location.pathname),
        signal: controller.signal,
      });

      if (!res.isAvailable) {
        setUnavailableMessage(res.reply || "The assistant is currently unavailable.");
        return;
      }

      if (res.error) {
        setHasError(true);
        return;
      }

      setMessages([
        ...nextMessages,
        {
          role: "assistant",
          content: res.reply,
          needsClarification: res.needsClarification,
          chart: res.chart,
          aiInputId: res.aiInputId,
        },
      ]);
    } catch (err) {
      if (isAbortError(err)) {
        setCancelledNote(true);
      } else {
        setHasError(true);
      }
    } finally {
      abortRef.current = null;
      setIsLoading(false);
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
  };

  const resetWidget = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setInput("");
    setIsLoading(false);
    setHasError(false);
    setUnavailableMessage(null);
    setCancelledNote(false);
    try {
      sessionStorage.removeItem(HISTORY_KEY);
    } catch {
      /* ignore */
    }
  };

  const retryLast = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return;
    // Drop the failed user turn so send() re-appends it cleanly.
    const withoutLastUser = messages.slice(0, messages.lastIndexOf(lastUser));
    setMessages(withoutLastUser);
    void send(lastUser.content);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  };

  // The launcher now lives in the app header (CustomTopBar); render nothing until opened.
  if (!isOpen) {
    return <></>;
  }

  const isEmpty = messages.length === 0;
  const inputDisabled = isLoading || !!unavailableMessage;
  const loadingStatus =
    statusTick >= LONG_RUN_AFTER_TICKS
      ? LONG_RUN_STATUS
      : LOADING_STATUSES[statusTick % LOADING_STATUSES.length];

  const closeWidget = () => {
    setIsFullscreen(false);
    close();
  };

  return (
    <div
      className={`chatbot-panel${isFullscreen ? " chatbot-panel--fullscreen" : ""}`}
      role="dialog"
      aria-label={botName}
    >
      <div className="chatbot-header">
        <div className="chatbot-title">
          <Bot size={18} />
          <span className="heading" style={{ color: "#ffffff" }}>{botName}</span>
          <span className="chatbot-beta">Beta</span>
          <span className="chatbot-online-dot" title="Online" />
        </div>
        <button
          className="chatbot-close"
          aria-label="Reset conversation"
          title="Reset conversation"
          onClick={resetWidget}
        >
          <RotateCcw size={16} />
        </button>
        <button
          className="chatbot-close"
          aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
          title={isFullscreen ? "Exit full screen" : "Full screen"}
          onClick={() => setIsFullscreen((f) => !f)}
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
        <button className="chatbot-close" aria-label="Close" onClick={closeWidget}>
          <X size={18} />
        </button>
      </div>

      <div className="chatbot-messages">
        {isEmpty && !unavailableMessage && (
          <div className="chatbot-welcome">
            <div className="chatbot-bubble bot normal_text">{welcomeMessage}</div>
            <div className="chatbot-chips">
              {suggestedPrompts.map((prompt) => (
                <button key={prompt} className="chatbot-chip" onClick={() => void send(prompt)}>
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`chatbot-bubble ${m.role === "user" ? "user" : "bot"}${
              m.needsClarification ? " clarify" : ""
            } normal_text`}
          >
            {m.role === "user" ? (
              m.content
            ) : (
              <>
                {m.needsClarification && (
                  <div className="chatbot-clarify-label sub_text">Need a bit more detail</div>
                )}
                <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{m.content}</Markdown>
                <ResponseActions
                  markdown={m.content}
                  reportName={reportNameForIndex(messages, i)}
                  chart={m.chart}
                  aiInputId={m.aiInputId}
                />
              </>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="chatbot-bubble bot chatbot-working">
            <span className="chatbot-typing">
              <span />
              <span />
              <span />
            </span>
            <span className="chatbot-status sub_text">{loadingStatus}</span>
            <button
              className="chatbot-cancel"
              onClick={cancel}
              aria-label="Cancel"
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {cancelledNote && (
          <div className="chatbot-note sub_text">Request cancelled.</div>
        )}

        {hasError && (
          <div className="chatbot-error">
            <div className="chatbot-bubble bot normal_text">
              Sorry, something went wrong. Please try again.
            </div>
            <button className="chatbot-retry" onClick={retryLast}>
              Retry
            </button>
          </div>
        )}

        {unavailableMessage && (
          <div className="chatbot-unavailable normal_text">{unavailableMessage}</div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chatbot-input-area">
        <textarea
          ref={textareaRef}
          className="chatbot-input"
          rows={1}
          placeholder="Ask about your portfolio..."
          value={input}
          disabled={inputDisabled}
          onChange={(e) => {
            setInput(e.target.value);
            autoGrow();
          }}
          onKeyDown={onKeyDown}
        />
        <button
          className="chatbot-send"
          aria-label="Send"
          disabled={inputDisabled || !input.trim()}
          onClick={() => void send(input)}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default ChatbotWidget;
